import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Redirect, useLocation } from 'react-router-dom';

import { Box, Tab, Tabs, useTheme } from '@mui/material';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { PENDING_CHANGES_SUBTAB } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/PendingChangesDrawer/PendingChangesDrawer.constants';
import LoanTabLayout from 'app/pages/loanApplication/LoanTabLayout';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import { compose } from 'redux';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import {
  ActivitiesSubtab,
  ActivitiesSubtabRouteParam,
  LoanApplicationTabRouteParam,
} from 'constants/loanApplication';
import useAbortController from 'hooks/useAbortController';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useScrollIntoView from 'hooks/useScrollIntoView';
import { getConditionalArray } from 'utils/data';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import {
  getApplicationBorrowersAction,
  getSubjectPropertyAction,
  setActiveTabWithNavigationAction,
} from 'store/loanApplications/actions';
import { selectFilteredActivitiesSubtabs } from 'store/loanApplications/selectors';

import { getStyles } from './Activities.styles';
import ActivitiesList from './ActivitiesList';
import ContactsColumn from './ContactsColumn';
import { ActivitiesContext } from './hooks/useActivitiesContext';

const tabTabsStyles = {
  backgroundColor: ({ palette }) => palette.background.default,
};

const tabTabStyles = {
  '&:not(:last-child)': {
    borderRight: ({ palette }) => `1px solid ${palette.divider}`,
  },
};

const Activities = ({ setIsLoading, isLoading }) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const dispatch = useDispatch();
  const { state: navigationState } = useLocation();

  const { scrollContainerRef: scrollActivitiesListRef, triggerScrollIntoView } =
    useScrollIntoView();

  const { applicationId, subtab } = useParams();

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const { getAbortSignal, resetAbortSignal, emitAbort } = useAbortController();

  const filteredSubtabs = useSelector(selectFilteredActivitiesSubtabs);
  const activitiesDataContextValue = useMemo(
    () => ({ triggerScrollIntoView }),
    [triggerScrollIntoView],
  );

  const handleTabChange = useCallback(
    (__, value) => {
      dispatch(
        setActiveTabWithNavigationAction({
          activeTab: LoanApplicationTabRouteParam.ACTIVITIES,
          activeSubtab: value,
          applicationId,
        }),
      );

      emitAbort();
      resetAbortSignal();
    },
    [emitAbort, resetAbortSignal, dispatch, applicationId],
  );

  const isManageCallsAllowed = useSelector(
    selectIsClaimAllowed(COMPOSED_CLAIMS.LOAN_APPLICATION_CALLS),
  );

  const hiddenTabs = useMemo(
    () => [
      ...getConditionalArray(!isManageCallsAllowed, [ActivitiesSubtabRouteParam.CALLS]),
      // Internal Chat is intentionally kept hidden (product decision) — the
      // subtab is retained but not exposed.
      ActivitiesSubtabRouteParam.INTERNAL_CHAT,
    ],
    [isManageCallsAllowed],
  );

  const activitiesTabsList = useMemo(
    () => Object.values(ActivitiesSubtab).filter(({ tabRoute }) => !hiddenTabs.includes(tabRoute)),
    [hiddenTabs],
  );

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    Promise.all([
      dispatch(getSubjectPropertyAction({ applicationId })),
      dispatch(getApplicationBorrowersAction({ applicationId })),
    ])
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
        setIsDataLoaded(true);
      });
  }, [applicationId, dispatch, setIsLoading]);

  const isPendingChangesDrawerOpen = subtab === PENDING_CHANGES_SUBTAB;
  const isRedirectNeeded =
    !isPendingChangesDrawerOpen && !_.some(filteredSubtabs, i => i === subtab);

  if (isRedirectNeeded) {
    const applicationPathname = LoansPaths.applicationManagement.generatePath({
      [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
      [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.ACTIVITIES,
      [LOANS_ROUTING_PARAMS.subtab]: ActivitiesSubtabRouteParam.ALL,
    });

    // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
    const to = {
      pathname: applicationPathname,
      state: navigationState,
    };

    return <Redirect to={to} />;
  }

  return (
    <LoanTabLayout
      isActivitiesPage
      containerClassname="flex flex-col"
      content={
        <>
          <Tabs
            indicatorColor="secondary"
            sx={tabTabsStyles}
            textColor="secondary"
            value={subtab}
            variant="scrollable"
            onChange={handleTabChange}
          >
            {activitiesTabsList.map(({ tabName, tabRoute }, index) => (
              <Tab key={index} label={tabName} sx={tabTabStyles} value={tabRoute} />
            ))}
          </Tabs>
          <Box className="flex items-stretch w-full" sx={classes.activities}>
            <ActivitiesContext.Provider value={activitiesDataContextValue}>
              <ContactsColumn activeTab={subtab} isLoading={isLoading || !isDataLoaded} />
              <ActivitiesList
                activeTab={subtab}
                getAbortSignal={getAbortSignal}
                scrollContainerRef={scrollActivitiesListRef}
              />
            </ActivitiesContext.Provider>
          </Box>
        </>
      }
      isRowLayout={false}
    />
  );
};

Activities.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(Activities);
